import { useState, useCallback, useEffect } from 'react';
import { useDataProvider, useGetList } from 'react-admin';
import type { Task } from './types';

interface UseMyTasksOptions {
  filters?: {
    status?: string[];
    namespace?: string[];
    subsystem?: string[];
    priority?: string[];
  };
  sort?: { field: 'expectedCompletionDate' | 'priority' | 'createdAt'; order: 'asc' | 'desc' };
  page?: number;
  perPage?: number;
}

export function useMyTasks(options: UseMyTasksOptions = {}) {
  const { filters = {}, sort = { field: 'expectedCompletionDate', order: 'asc' }, page = 1, perPage = 25 } = options;
  const dataProvider = useDataProvider();

  const { data: tasksData, total, isLoading, error, refetch } = useGetList<Task>('tasks', {
    pagination: { page, perPage },
    sort: { field: sort.field, order: sort.order.toUpperCase() as 'ASC' | 'DESC' },
    filter: {},
  });

  const [completing, setCompleting] = useState(false);

  const completeTask = useCallback(async (
    taskId: string,
    completion: { notes?: string; outputData?: Record<string, unknown> }
  ) => {
    setCompleting(true);
    try {
      await dataProvider.create('taskCompletions', {
        data: {
          taskId,
          completionNumber: 1,
          completedBy: { userId: 'current', userName: 'Current User', userRole: 'HR Officer' },
          outcome: 'Completed',
          notes: completion.notes || null,
          outputData: completion.outputData || null,
          completionTime: { startedAt: null, completedAt: new Date().toISOString(), durationMinutes: null },
          withinDeadline: null,
          workflowSignal: {
            signal: 'TASK_COMPLETED',
            workflowInstanceId: tasksData?.find(t => t.id === taskId)?.workflowInstanceId || '',
            resumeWorkflow: true,
          },
          metadata: { createdAt: new Date().toISOString(), createdBy: 'current' },
        },
      });

      const taskIdx = tasksData?.findIndex(t => t.id === taskId) ?? -1;
      if (taskIdx !== -1 && tasksData) {
        await dataProvider.update('tasks', {
          id: taskId,
          data: { ...tasksData[taskIdx], status: 'Completed', actualCompletionDate: new Date().toISOString() },
          previousData: tasksData[taskIdx],
        });
      }

      refetch();
    } finally {
      setCompleting(false);
    }
  }, [dataProvider, tasksData, refetch]);

  const tasks = (tasksData || []).filter((task: Task) => {
    if (filters.status?.length && !filters.status.includes(task.status)) return false;
    if (filters.namespace?.length && !filters.namespace.includes(task.namespace)) return false;
    if (filters.subsystem?.length && !filters.subsystem.includes(task.subsystem)) return false;
    if (filters.priority?.length && !filters.priority.includes(task.priority)) return false;
    return true;
  });

  return {
    tasks,
    total: tasks.length,
    isLoading,
    error,
    completing,
    completeTask,
    refetch,
    pagination: {
      page,
      perPage,
      totalPages: Math.ceil((total ?? 0) / perPage),
    },
  };
}

export function useTask(taskId: string) {
  const dataProvider = useDataProvider();
  const [task, setTask] = useState<Task | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!taskId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    dataProvider.getOne('tasks', { id: taskId })
      .then((result: { data: Task }) => setTask(result.data))
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [taskId, dataProvider]);

  const complete = useCallback(async (completion: { notes?: string; outputData?: Record<string, unknown> }) => {
    if (!task) return;
    await dataProvider.create('taskCompletions', {
      data: {
        taskId: task.id,
        completionNumber: 1,
        completedBy: { userId: 'current', userName: 'Current User', userRole: 'HR Officer' },
        outcome: 'Completed',
        notes: completion.notes || null,
        outputData: completion.outputData || null,
        completionTime: { startedAt: null, completedAt: new Date().toISOString(), durationMinutes: null },
        withinDeadline: null,
        workflowSignal: {
          signal: 'TASK_COMPLETED',
          workflowInstanceId: task.workflowInstanceId,
          resumeWorkflow: true,
        },
        metadata: { createdAt: new Date().toISOString(), createdBy: 'current' },
      },
    });
    setTask({ ...task, status: 'Completed', actualCompletionDate: new Date().toISOString() });
  }, [task, dataProvider]);

  return { task, loading, error, complete };
}